/**
 * ClientsMentionedField: the weekly call dialog's "Clients mentioned".
 * A search by name over this care manager's caseload (the Workspace
 * caseload roster: the clients they lead and the ones they share); a
 * match picked joins the list below, tagged Other until a tag is chosen.
 * Each picked client takes exactly one of three tags, On hospice, Passed
 * away or Other, as a compact switch. On hospice asks for the day the
 * client went on hospice ("Since"), Passed away for the date of death;
 * both are required and no later than today, and both open on what the
 * client's record already holds.
 *
 * A seat that does not set a client's status (canSetRecord false, the
 * manage_client_identity gate) sees On hospice and Passed away greyed,
 * with the quiet line "Supervisors and the office set this.", and may
 * still tag Other. A mention already stored with one of those tags stays
 * as it is for that seat: its date reads, never edits.
 *
 * Each picked client is one aligned row on a wide screen: the name (cut
 * with an ellipsis), the switch, Remove; the date, when its tag asks for
 * one, sits under the switch. On a phone the switch and the date take a
 * line each under the name.
 */

import { useId, useMemo, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/primitives/Button';
import { DateInput } from '@/components/primitives/DateInput';
import { SearchField } from '@/components/primitives/SearchField';
import { SegmentedToggle, type SegmentedOption } from '@/components/primitives/SegmentedToggle';
import { FLD_LABEL } from '@/components/primitives/modalFormStyles';
import { MENTION_DATE_LABEL, MENTION_TAG_LABEL, STATUS_SEATS_SET_THIS, tagSetsRecord } from '@/lib/callMentions';
import type { ClientCallMentionTag } from '@/lib/capacityApi';

/** One picked client as the dialog holds it. `date` is '' until typed. */
export interface MentionDraft {
  client_id: string;
  display_name: string;
  tag: ClientCallMentionTag;
  date: string;
}

export interface CaseloadPick {
  id: string;
  display_name: string;
}

/** How many matches the search lists at once. */
const MATCHES_MAX = 6;

/** The date a draft must carry and does not: required for the two
 *  record-writing tags, no later than today. */
export function mentionDateProblem(d: MentionDraft, today: string): 'missing' | 'future' | null {
  if (!tagSetsRecord(d.tag)) return null;
  if (d.date === '') return 'missing';
  return d.date > today ? 'future' : null;
}

const DATE_SENTENCE: Record<'hospice' | 'passed_away', Record<'missing' | 'future', string>> = {
  hospice: {
    missing: 'Enter the day they went on hospice.',
    future: "The hospice date can't be in the future. Enter today or an earlier day.",
  },
  passed_away: {
    missing: 'Enter the date of death.',
    future: "The date of death can't be in the future. Enter today or an earlier day.",
  },
};

function tagOptions(
  canSetRecord: boolean,
): readonly [SegmentedOption<ClientCallMentionTag>, SegmentedOption<ClientCallMentionTag>, SegmentedOption<ClientCallMentionTag>] {
  return [
    { value: 'hospice', label: MENTION_TAG_LABEL.hospice, suffix: 'hospice', checkedLook: 'gold', disabled: !canSetRecord },
    { value: 'passed_away', label: MENTION_TAG_LABEL.passed_away, suffix: 'passed-away', checkedLook: 'ink', disabled: !canSetRecord },
    { value: 'other', label: MENTION_TAG_LABEL.other, suffix: 'other' },
  ];
}

export function ClientsMentionedField({
  caseload,
  value,
  onChange,
  canSetRecord,
  readOnly,
  showProblems,
  today,
  recordDate,
}: {
  caseload: CaseloadPick[];
  value: MentionDraft[];
  onChange: (next: MentionDraft[]) => void;
  canSetRecord: boolean;
  /** Someone else has the week open: the list reads, nothing edits. */
  readOnly: boolean;
  /** A save was tried: missing and future dates say so. */
  showProblems: boolean;
  today: string;
  /** What the record holds for a client and tag, to open a new pick on. */
  recordDate: (clientId: string, tag: 'hospice' | 'passed_away') => string | null;
}): ReactNode {
  const labelId = useId();
  const [q, setQ] = useState('');
  const picked = useMemo(() => new Set(value.map((d) => d.client_id)), [value]);
  const needle = q.trim().toLowerCase();
  const matches =
    needle === ''
      ? []
      : caseload.filter((c) => !picked.has(c.id) && c.display_name.toLowerCase().includes(needle)).slice(0, MATCHES_MAX);
  const options = tagOptions(canSetRecord);

  const add = (c: CaseloadPick): void => {
    onChange([...value, { client_id: c.id, display_name: c.display_name, tag: 'other', date: '' }]);
    setQ('');
  };
  const update = (id: string, patch: Partial<MentionDraft>): void => {
    onChange(value.map((d) => (d.client_id === id ? { ...d, ...patch } : d)));
  };
  const remove = (id: string): void => onChange(value.filter((d) => d.client_id !== id));

  return (
    <div data-testid="availability-mentions-field">
      <p id={labelId} className={FLD_LABEL}>Clients mentioned</p>
      {!readOnly && (
        <div className="relative">
          <SearchField
            value={q}
            onChange={setQ}
            placeholder="Search by name"
            aria-labelledby={labelId}
            testId="availability-mentions-search"
          />
          {needle !== '' && (
            <div
              className="mt-1 flex flex-col rounded-base border border-sage-800/[12%] bg-paper py-1 shadow-sh1"
              data-testid="availability-mentions-matches"
            >
              {matches.length === 0 ? (
                <p className="px-3 py-1.5 text-xs text-ink-soft">Try another spelling.</p>
              ) : (
                matches.map((c) => (
                  <Button
                    key={c.id}
                    variant="ghost"
                    size="sm"
                    className="!justify-start !rounded-none !px-3 text-left"
                    data-testid={`availability-mentions-match-${c.id}`}
                    onClick={() => add(c)}
                  >
                    <span className="truncate">{c.display_name}</span>
                  </Button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {value.length > 0 && (
        <ul className="mt-2 flex flex-col divide-y divide-sage-800/[8%]" data-testid="availability-mentions-list">
          {value.map((d) => {
            const problem = showProblems ? mentionDateProblem(d, today) : null;
            const dateId = `mention-date-${d.client_id}`;
            const recordTag = tagSetsRecord(d.tag) ? d.tag : null;
            // A record-writing tag on a seat that cannot set the record is
            // one already stored: it reads.
            const dateLocked = readOnly || (recordTag !== null && !canSetRecord);
            return (
              <li
                key={d.client_id}
                data-testid={`availability-mention-${d.client_id}`}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5 py-2 sm:grid-cols-[minmax(0,1fr)_auto_auto]"
              >
                <span className="col-start-1 row-start-1 min-w-0 truncate text-sm font-medium text-sage-800" title={d.display_name}>
                  {d.display_name}
                </span>
                <div className="col-span-2 row-start-2 sm:col-span-1 sm:col-start-2 sm:row-start-1">
                  <SegmentedToggle<ClientCallMentionTag>
                    options={options}
                    value={d.tag}
                    onChange={(tag) => {
                      if (readOnly) return;
                      const date = tagSetsRecord(tag) ? (recordDate(d.client_id, tag) ?? '') : '';
                      update(d.client_id, { tag, date });
                    }}
                    name={`Tag for ${d.display_name}`}
                    compact
                    busy={readOnly}
                    testIdPrefix={`availability-mention-tag-${d.client_id}`}
                  />
                </div>
                <div className="col-start-2 row-start-1 justify-self-end sm:col-start-3">
                  {!readOnly && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="!px-1.5"
                      aria-label={`Remove ${d.display_name}`}
                      data-testid={`availability-mention-remove-${d.client_id}`}
                      onClick={() => remove(d.client_id)}
                    >
                      <X aria-hidden="true" className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {recordTag !== null && (
                  <div className="col-span-2 row-start-3 flex items-center gap-1.5 sm:col-span-1 sm:col-start-2 sm:row-start-2">
                    {/* One label width, so every date box lines up. */}
                    <label htmlFor={dateId} className="w-[5rem] shrink-0 text-xs text-ink-soft">
                      {MENTION_DATE_LABEL[recordTag]}
                    </label>
                    <DateInput
                      id={dateId}
                      size="compact"
                      className="w-[9.5rem]"
                      max={today}
                      value={d.date}
                      readOnly={dateLocked}
                      required
                      aria-invalid={problem ? 'true' : undefined}
                      aria-describedby={problem ? `${dateId}-error` : undefined}
                      data-testid={`availability-mention-date-${d.client_id}`}
                      onChange={(e) => update(d.client_id, { date: e.target.value })}
                    />
                  </div>
                )}
                {problem && recordTag !== null && (
                  <p id={`${dateId}-error`} role="alert" className="col-span-full text-xs text-pepper-deep">
                    {DATE_SENTENCE[recordTag][problem]}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!canSetRecord && value.length > 0 && (
        <p className="mt-1 text-xs text-ink-soft" data-testid="availability-mentions-office-line">
          {STATUS_SEATS_SET_THIS}
        </p>
      )}
    </div>
  );
}
